import type { Service } from '@salonomy/types';
import { serviceSpanMin } from '../../data/services.js';
import { addDays, longDate, spanLabel } from '../../lib/time.js';

/**
 * WS7 phase 3 — client-side cadence helpers for the recurring-series UI. These are PRESENTATION ONLY:
 * the authoritative occurrence projection + conflict classification happens server-side (the create/
 * edit endpoints run every occurrence through the availability engine). We project the same dates
 * locally so the operator can preview the standing slots ("every other Tuesday, 6 dates") before they
 * commit — the server's conflict list is what's trusted, never this preview.
 *
 * We project by wall-clock DATE (add intervalWeeks×7 days to the YYYY-MM-DD), not by adding raw ms to
 * an instant, so a DST shift across the series keeps the same local weekday — matching how the API
 * re-anchors the cadence in the salon timezone.
 */

export const MAX_OCCURRENCES = 52;
export const MAX_INTERVAL_WEEKS = 26;

export interface CadenceOptions {
  /** First occurrence date in the salon zone, "YYYY-MM-DD". */
  firstDate: string;
  /** Weeks between occurrences (1 = weekly, 2 = fortnightly, 4 = every 4 weeks). */
  intervalWeeks: number;
  /** Total occurrences requested. */
  occurrenceCount: number;
}

/** Project the local DATES of a cadence (used for the preview list, not for booking). */
export function projectDates(opts: CadenceOptions): string[] {
  const out: string[] = [];
  const count = Math.max(0, Math.min(opts.occurrenceCount, MAX_OCCURRENCES));
  const interval = Math.max(1, Math.min(opts.intervalWeeks, MAX_INTERVAL_WEEKS));
  for (let i = 0; i < count; i++) {
    out.push(addDays(opts.firstDate, i * interval * 7));
  }
  return out;
}

/** "every week", "every 2 weeks", "every 4 weeks". */
export function cadenceLabel(intervalWeeks: number): string {
  if (intervalWeeks <= 1) return 'every week';
  if (intervalWeeks === 2) return 'every 2 weeks';
  return `every ${intervalWeeks} weeks`;
}

/** A plain-English summary, e.g. "Every 2 weeks · 6 dates · 45 min with Amara K." */
export function seriesSummary(
  intervalWeeks: number,
  occurrenceCount: number,
  service: Pick<Service, 'durationMin' | 'processingMin'> | undefined,
  staffName: string,
): string {
  const cadence = cadenceLabel(intervalWeeks);
  const dates = `${occurrenceCount} ${occurrenceCount === 1 ? 'date' : 'dates'}`;
  const span = service ? `${serviceSpanMin(service)} min` : '';
  return [
    cadence.charAt(0).toUpperCase() + cadence.slice(1),
    dates,
    span,
    staffName ? `with ${staffName}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Long date label for a projected occurrence, e.g. "Tuesday 24 June". */
export function occurrenceDateLabel(date: string, tz: string): string {
  return longDate(date, tz);
}

/** Re-export so the conflict list can label a fitted/conflicting time band consistently. */
export { spanLabel };
